import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const ScalingRootPlaning = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Scaling & Root Planing Fort Worth TX | Deep Cleaning | Fort Worth Dental</title>
        <meta name="description" content="Professional scaling and root planing (deep cleaning) in Fort Worth TX. Treat gum disease and prevent tooth loss. Periodontal care experts. Call (817) 985-3337." />
        <meta name="keywords" content="scaling root planing fort worth, deep cleaning fort worth tx, gum disease treatment, periodontal scaling, teeth cleaning" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examining.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Scaling & Root Planing in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Deep Cleaning for Gum Disease</p>
            <p className="text-sm mt-4 opacity-90">Deep Cleaning | Non-Surgical Gum Treatment | Periodontal Therapy</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Scaling & Root Planing */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/scaling-new-1.jpg" 
                  alt="Scaling and Root Planing" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Scaling & Root Planing?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  When gum disease is not diagnosed and confronted it can reach a point that it will require action by the dentist. A common procedure that is performed by a dentist in the fight against gum disease is scaling and root planing. The basic plan is to remove the plaque and tartar from underneath the infected gum line. Next, the dentist will plane the surface of the teeth in the affected area in order to help the gums attach to the teeth again.
                </p>
              </div>
            </div>
          </section>

          {/* What Is Involved In The Procedure */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is Involved In The Procedure?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  The dentist will typically begin the process with a local anesthetic. He then employs the use of an instrument known as a scaler to remove the plaque and tarter that has built up underneath the gum line in the infected areas. After cleaning out the gums, the dentist will plane and smooth the root surfaces of the tooth to allow the gums to quickly and easily reattach to the surface of the teeth. Some dentists also place antibiotic fibers between the patient's gums and teeth in order to help the healing process and reduce the possibility of infection. If the dentist uses the antibiotic fibers, he will removed them about a week after he has done the scaling and root planing.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  The use of anesthesia may cause your lips and gums to be numb for a few hours after your treatment. It will, however, make the treatment more comfortable.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/scaling-new-2.jpg" 
                  alt="Scaling Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Can I Avoid Scaling and Planing */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/scaling-new-1.jpg" 
                  alt="Prevention" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">Can I Avoid Scaling and Planing?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  An aggressive and thorough daily oral hygiene will usually help a patient avoid gum disease. However, if gum disease begins to develop, scaling and planing is an effective way to treat the gum disease without resorting to more serious treatments.
                </p>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Benefits of Scaling & Root Planing</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Treats gum disease without surgery</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Removes bacteria below the gum line</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Promotes gum healing and reattachment</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Reduces pocket depth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevents tooth loss from gum disease</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Non-surgical alternative to flap surgery</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Improves overall oral health</span>
              </li>
            </ul>
          </section>

          {/* After Treatment */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">What To Expect After Treatment</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              After your scaling and root planing procedure:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Your gums may be tender for a few days</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>You may experience some sensitivity to hot and cold</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Your teeth may feel slightly loose initially as gums heal</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Follow your dentist's instructions for home care</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Schedule regular follow-up appointments for maintenance</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Scaling & Root Planing?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced periodontal team provides gentle, effective deep cleaning treatments.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Gentle Care</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Stop Gum Disease with Deep Cleaning</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for scaling and root planing in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Scaling & Root Planing Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default ScalingRootPlaning;